import { useState } from 'react'

import { api, downloadBlob } from '../api/client'
import type { SupplierDocument, SupplierDocumentStatement, SupplierSbomComparison } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { GIUSTIFICAZIONI, etichettaStatoVex } from './CancelloSicurezza'
import { Card, ErrorBanner, Spinner } from './common'
import { DataTable, type Column } from './DataTable'

/**
 * The documents a supplier gave about its products (supplier_document_service):
 * an SBOM compared with what the agent finds installed; a VEX or a CSAF
 * advisory whose «not affected» statements become statements of the supplier
 * when applied (only those with a justification).
 */

function giornoTra(giorni: number): string {
  return new Date(Date.now() + giorni * 86400000).toISOString().slice(0, 10)
}

export function DocumentiFornitore({ supplierId, scrive }: { supplierId: string; scrive: boolean }) {
  const dati = useAsync(() => api.supplierDocuments(supplierId), [supplierId])
  const [tipo, setTipo] = useState<SupplierDocument['kind']>('sbom')
  const [prodotto, setProdotto] = useState('')
  const [versioni, setVersioni] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [aperto, setAperto] = useState<SupplierDocument | null>(null)
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)

  async function carica() {
    if (!file) return
    setLavoro(true)
    setErrore(null)
    try {
      const d = await api.uploadSupplierDocument(supplierId, {
        kind: tipo, document: await file.text(), product: prodotto, versions: versioni, filename: file.name,
      })
      setFile(null)
      await dati.reload()
      setAperto(d)
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  async function scarica(d: SupplierDocument) {
    try {
      const blob = await downloadBlob(api.supplierDocumentOriginalPath(d.id))
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = d.filename || `${d.kind}.json`
      a.click()
      URL.revokeObjectURL(a.href)
    } catch (e) {
      setErrore(e)
    }
  }

  const colonne: Column<SupplierDocument>[] = [
    { key: 'kind', header: t('Tipo'), value: (d) => `${d.kind.toUpperCase()} ${d.format}` },
    { key: 'product', header: t('Prodotto'), value: (d) => `${d.product} ${d.versions}` },
    { key: 'filename', header: t('File'), value: (d) => d.filename,
      render: (d) => <>{d.filename}{d.summary.system && <> <span className="badge badge--info">{t('di CENSO')}</span></>}</> },
    { key: 'summary', header: t('Letto'), value: (d) => d.kind === 'sbom' ? (d.summary.component_count ?? 0) : (d.summary.statement_count ?? 0),
      render: (d) => d.kind === 'sbom'
        ? t('{n} componenti, {v} vulnerabili', { n: d.summary.component_count ?? 0, v: d.summary.vulnerable ?? 0 })
        : t('{n} dichiarazioni, {na} «non interessato»', { n: d.summary.statement_count ?? 0, na: d.summary.not_affected ?? 0 }) },
    { key: 'created_at', header: t('Caricato'), value: (d) => d.created_at ?? '',
      render: (d) => (d.created_at ? new Date(d.created_at).toLocaleDateString(localeCorrente()) : '—') },
    { key: 'azioni', header: '', sort: false, searchable: false, render: (d) => (
      <span className="estensione-azioni">
        <button type="button" className="secondary" onClick={() => setAperto(d)}>{d.kind === 'sbom' ? t('Confronta') : t('Dichiarazioni')}</button>
        <button type="button" className="secondary" onClick={() => void scarica(d)}>{t('Originale')}</button>
        {scrive && !d.summary.system && <button type="button" className="secondary" onClick={() => {
          if (window.confirm(t('Eliminare il documento? Le dichiarazioni già applicate restano.'))) {
            void api.deleteSupplierDocument(d.id).then(() => { setAperto(null); return dati.reload() }).catch(setErrore)
          }
        }}>{t('Elimina')}</button>}
      </span>
    ) },
  ]

  return (
    <>
      <Card title={t('Documenti del fornitore')}>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Un SBOM (CycloneDX o SPDX) si confronta con quello che l’agente trova installato; una VEX (CycloneDX o OpenVEX) o un avviso CSAF porta le dichiarazioni del fornitore, che diventano tue quando le applichi.')}
        </p>
        <ErrorBanner error={errore ?? dati.error} />
        {scrive && (
          <div className="eagle-report__riga" style={{ alignItems: 'flex-end' }}>
            <label className="field" style={{ maxWidth: 150 }}><span className="field__label">{t('Tipo')}</span>
              <select value={tipo} onChange={(e) => setTipo(e.target.value as SupplierDocument['kind'])}>
                <option value="sbom">SBOM</option><option value="vex">VEX</option><option value="csaf">CSAF</option>
              </select></label>
            <label className="field"><span className="field__label">{t('Prodotto')}</span>
              <input value={prodotto} onChange={(e) => setProdotto(e.target.value)} placeholder={t('come si chiama installato')} /></label>
            <label className="field" style={{ maxWidth: 160 }}><span className="field__label">{t('Versioni')}</span>
              <input value={versioni} onChange={(e) => setVersioni(e.target.value)} /></label>
            <label className="field"><span className="field__label">{t('File JSON')}</span>
              <input type="file" accept=".json,application/json" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></label>
            <button type="button" disabled={!file || lavoro} onClick={() => void carica()}>{lavoro ? t('Lettura…') : t('Carica')}</button>
          </div>
        )}
        {dati.loading && !dati.data ? <Spinner /> : (
          <DataTable rows={dati.data ?? []} columns={colonne} rowKey={(d) => d.id} conteggio={{ nome: t('documenti') }}
                     emptyText={t('Nessun documento del fornitore.')} />
        )}
      </Card>
      {aperto && (aperto.kind === 'sbom'
        ? <Confronto documento={aperto} onChiudi={() => setAperto(null)} />
        : <Dichiarazioni documento={aperto} scrive={scrive} onChiudi={() => { setAperto(null); void dati.reload() }} />)}
    </>
  )
}

function Confronto({ documento, onChiudi }: { documento: SupplierDocument; onChiudi: () => void }) {
  const dati = useAsync<SupplierSbomComparison>(() => api.supplierDocumentComparison(documento.id), [documento.id])
  return (
    <Card title={t('SBOM del fornitore e installato: {p}', { p: documento.product || documento.filename })}
          actions={<button type="button" className="secondary" onClick={onChiudi}>{t('Chiudi')}</button>}>
      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : dati.data && (
        dati.data.applications === 0 ? (
          <p className="field__hint">{t('Nessuna applicazione installata con questo nome: l’agente con l’SBOM acceso la troverebbe sulle macchine dove gira.')}</p>
        ) : (
          <>
            <p>{t('{a} applicazioni installate · {d} componenti dichiarati · {i} trovati · {s} uguali', {
              a: dati.data.applications, d: dati.data.declared, i: dati.data.installed, s: dati.data.same })}</p>
            {dati.data.different_versions.length > 0 && (
              <>
                <h4 className="ruoli__titoletto">{t('Versioni diverse')}</h4>
                <ul className="elenco-semplice">
                  {dati.data.different_versions.map((x) => (
                    <li key={x.name}><span className="mono">{x.name}</span> — {t('dichiarata {d}, installata {i}', { d: x.declared.join(', '), i: x.installed.join(', ') })}</li>
                  ))}
                </ul>
              </>
            )}
            {dati.data.only_installed.length > 0 && (
              <>
                <h4 className="ruoli__titoletto">{t('Installati e non dichiarati')}</h4>
                <p className="mono eagle-piccolo">{dati.data.only_installed.join(', ')}</p>
              </>
            )}
            {dati.data.only_declared.length > 0 && (
              <>
                <h4 className="ruoli__titoletto">{t('Dichiarati e non trovati')}</h4>
                <p className="mono eagle-piccolo">{dati.data.only_declared.join(', ')}</p>
              </>
            )}
          </>
        )
      )}
    </Card>
  )
}

function Dichiarazioni({ documento, scrive, onChiudi }: { documento: SupplierDocument; scrive: boolean; onChiudi: () => void }) {
  const dati = useAsync<SupplierDocumentStatement[]>(() => api.supplierDocumentStatements(documento.id), [documento.id])
  const [scadenza, setScadenza] = useState(giornoTra(180))
  const [esito, setEsito] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const colonne: Column<SupplierDocumentStatement>[] = [
    { key: 'vuln_id', header: t('Vulnerabilità'), mono: true },
    { key: 'product', header: t('Prodotto'), value: (s) => `${s.product} ${s.version}` },
    { key: 'status', header: t('Stato'), value: (s) => etichettaStatoVex(s.status) },
    { key: 'justification', header: t('Perché'), value: (s) => GIUSTIFICAZIONI.find(([k]) => k === s.justification)?.[1]() ?? '',
      render: (s) => (s.status === 'not_affected' && !s.justification
        ? <span className="badge badge--warn">{t('manca il perché: non si applica')}</span>
        : GIUSTIFICAZIONI.find(([k]) => k === s.justification)?.[1]() ?? '') },
  ]
  return (
    <Card title={t('Dichiarazioni del documento {f}', { f: documento.filename || documento.kind })}
          actions={<button type="button" className="secondary" onClick={onChiudi}>{t('Chiudi')}</button>}>
      <ErrorBanner error={errore ?? dati.error} />
      {esito && <p className="field__hint" role="status">✓ {esito}</p>}
      {scrive && (documento.summary.not_affected ?? 0) > 0 && (
        <div className="toolbar" style={{ alignItems: 'flex-end' }}>
          <label className="field" style={{ maxWidth: 200, margin: 0 }}><span className="field__label">{t('Valide fino al')}</span>
            <input type="date" value={scadenza} max={giornoTra(365)} onChange={(e) => setScadenza(e.target.value)} /></label>
          <button type="button" onClick={() => void api.applySupplierDocument(documento.id, `${scadenza}T23:59:00Z`)
            .then((r) => setEsito(t('{n} dichiarazioni applicate; {s} senza giustificazione non applicate.', { n: r.applied, s: r.without_justification })))
            .catch(setErrore)}>{t('Applica i «non interessato»')}</button>
        </div>
      )}
      {dati.loading && !dati.data ? <Spinner /> : (
        <DataTable rows={dati.data ?? []} columns={colonne} rowKey={(s) => `${s.vuln_id}|${s.product}|${s.version}|${s.status}`}
                   conteggio={{ nome: t('dichiarazioni') }} />
      )}
    </Card>
  )
}
